/**
 * Zustand slice for AI Citation Analysis state
 * Manages statements, citation analyses, and suggestions
 */

export const createAIAnalysisSlice = (set, get) => ({
  // Analysis state
  aiAnalysis: {
    // Phase 1: Statements
    statements: [],
    statementsById: {},
    
    // Phase 2: Citation analyses
    citationAnalyses: {},
    
    // Phase 3: Suggestions
    suggestions: {},
    
    // UI state
    isAnalyzing: false,
    analysisError: null,
    lastAnalyzedAt: null,
    highlightsVisible: true, // Toggle for showing/hiding statement highlights
    
    // Current view
    selectedStatementId: null,
  },

  // ============ Phase 1: Statement Analysis ============
  
  /**
   * Set extracted statements from backend
   */
  setAIStatements: (statements) => set((state) => {
    const statementsById = {};
    statements.forEach(stmt => {
      statementsById[stmt.id] = stmt;
    });
    
    return {
      aiAnalysis: {
        ...state.aiAnalysis,
        statements,
        statementsById,
        lastAnalyzedAt: new Date().toISOString()
      }
    };
  }),

  // ============ Phase 2: Citation Analysis ============
  
  /**
   * Set citation analysis results
   */
  setAICitationAnalyses: (analyses) => set((state) => {
    const citationAnalyses = {};
    analyses.forEach(analysis => {
      citationAnalyses[analysis.statementId] = analysis;
    });
    
    return {
      aiAnalysis: {
        ...state.aiAnalysis,
        citationAnalyses
      }
    };
  }),
  
  /**
   * Update a single citation analysis (after user adds a citation)
   */
  updateCitationAnalysis: (statementId, analysis) => set((state) => ({
    aiAnalysis: {
      ...state.aiAnalysis,
      citationAnalyses: {
        ...state.aiAnalysis.citationAnalyses,
        [statementId]: analysis
      }
    }
  })),

  // ============ Phase 3: Citation Suggestions ============
  
  /**
   * Set suggestions for a specific statement
   */
  setAISuggestions: (statementId, suggestions) => set((state) => ({
    aiAnalysis: {
      ...state.aiAnalysis,
      suggestions: {
        ...state.aiAnalysis.suggestions,
        [statementId]: suggestions
      }
    }
  })),

  // ============ UI State ============
  
  /**
   * Set analyzing state
   */
  setAIAnalyzing: (isAnalyzing) => set((state) => ({
    aiAnalysis: {
      ...state.aiAnalysis,
      isAnalyzing
    }
  })),

  /**
   * Set analysis error
   */
  setAIAnalysisError: (error) => set((state) => ({
    aiAnalysis: {
      ...state.aiAnalysis,
      analysisError: error
    }
  })),

  /**
   * Select a statement (for UI highlighting/scrolling)
   */
  setSelectedStatementId: (statementId) => set((state) => ({
    aiAnalysis: {
      ...state.aiAnalysis,
      selectedStatementId: statementId
    }
  })),

  /**
   * Toggle highlights visibility
   */
  toggleAIHighlights: () => set((state) => {
    const newVisibility = !state.aiAnalysis.highlightsVisible;
    console.log('🔄 toggleAIHighlights called');
    console.log('  Current visibility:', state.aiAnalysis.highlightsVisible);
    console.log('  New visibility:', newVisibility);
    
    // Unified system: toggle visibility of fragments with AI data (statementId attribute)
    const marks = document.querySelectorAll('span[data-element-name="report-fragment"][data-statement-id]');
    console.log('  Found fragments with AI analysis:', marks.length);
    
    marks.forEach((mark, index) => {
      if (newVisibility) {
        // Show highlights
        mark.removeAttribute('data-ai-hidden');
        console.log(`  ✅ Removed data-ai-hidden from span ${index + 1}`);
      } else {
        // Hide highlights
        mark.setAttribute('data-ai-hidden', 'true');
        console.log(`  ✅ Added data-ai-hidden to span ${index + 1}`);
      }
    });
    
    return {
      aiAnalysis: {
        ...state.aiAnalysis,
        highlightsVisible: newVisibility
      }
    };
  }),

  /**
   * Clear all analysis data
   */
  clearAIAnalysis: () => set((state) => ({
    aiAnalysis: {
      statements: [],
      statementsById: {},
      citationAnalyses: {},
      suggestions: {},
      isAnalyzing: false,
      analysisError: null,
      lastAnalyzedAt: null,
      highlightsVisible: true, // Reset to default
      selectedStatementId: null
    }
  })),

  // ============ Computed Getters ============

  /**
   * Get statement by ID
   */
  getAIStatement: (statementId) => {
    const state = get();
    return state.aiAnalysis.statementsById[statementId];
  },

  /**
   * Get citation analysis for a statement
   */
  getAICitationAnalysis: (statementId) => {
    const state = get();
    return state.aiAnalysis.citationAnalyses[statementId];
  },

  /**
   * Get suggestions for a statement
   */
  getAISuggestions: (statementId) => {
    const state = get();
    return state.aiAnalysis.suggestions[statementId];
  },

  /**
   * Get statements grouped by proof status
   */
  getStatementsByProofStatus: () => {
    const state = get();
    const { statements, citationAnalyses } = state.aiAnalysis;
    
    const groups = {
      FULLY_PROVEN: [],
      PARTIALLY_PROVEN: [],
      NO_CITATIONS: [],
      NO_PROOF: [],
      PROVEN_BY_REPORT: []
    };
    
    statements.forEach(stmt => {
      const analysis = citationAnalyses[stmt.id];
      if (analysis) {
        const status = analysis.proofStatus;
        if (groups[status]) {
          groups[status].push({
            ...stmt,
            analysis
          });
        }
      } else {
        // Not yet analyzed
        groups.NO_CITATIONS.push(stmt);
      }
    });
    
    return groups;
  }
});

